import React, { useState } from 'react'
import { ChevronRight, ChevronDown, Eye, EyeOff, Zap } from 'lucide-react'

interface ExplorationStep {
  id: string
  title: string
  content: string
  reveal?: string
  interactive?: React.ReactNode
}

interface InteractiveExplorationProps {
  title: string
  description: string
  steps: ExplorationStep[]
  onComplete?: () => void
}

const InteractiveExploration: React.FC<InteractiveExplorationProps> = ({
  title,
  description,
  steps,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [revealedSteps, setRevealedSteps] = useState<Set<number>>(new Set([0]))
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())

  const handleStepComplete = (stepIndex: number) => {
    const newCompleted = new Set(completedSteps)
    newCompleted.add(stepIndex)
    setCompletedSteps(newCompleted)

    // Reveal next step
    if (stepIndex < steps.length - 1) {
      const newRevealed = new Set(revealedSteps)
      newRevealed.add(stepIndex + 1)
      setRevealedSteps(newRevealed)
      setCurrentStep(stepIndex + 1)
    } else {
      onComplete?.()
    }
  }

  const toggleStepReveal = (stepIndex: number) => {
    const newRevealed = new Set(revealedSteps)
    if (newRevealed.has(stepIndex)) {
      newRevealed.delete(stepIndex)
    } else {
      newRevealed.add(stepIndex)
    }
    setRevealedSteps(newRevealed)
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6 my-6">
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Zap className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-700">{description}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Tiến độ khám phá</span>
          <span className="text-sm text-gray-500">
            {completedSteps.size}/{steps.length} bước
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(completedSteps.size / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const isRevealed = revealedSteps.has(index)
          const isCompleted = completedSteps.has(index)
          const isCurrent = currentStep === index
          
          return (
            <div
              key={step.id}
              className={`border-2 rounded-lg transition-all duration-300 ${
                isCurrent
                  ? 'border-purple-400 bg-white shadow-lg'
                  : isCompleted
                  ? 'border-green-300 bg-green-50'
                  : isRevealed
                  ? 'border-gray-200 bg-white'
                  : 'border-gray-100 bg-gray-50'
              }`}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => isRevealed && setCurrentStep(index)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isCurrent
                        ? 'bg-purple-500 text-white'
                        : isRevealed
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isCompleted ? '✓' : index + 1}
                    </div>
                    <h4 className={`font-semibold ${
                      isRevealed ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </h4>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {!isRevealed && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleStepReveal(index)
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {isRevealed && (
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                        isCurrent ? 'rotate-180' : ''
                      }`} />
                    )}
                  </div>
                </div>
              </div>

              {/* Step Content */}
              {isRevealed && isCurrent && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="pt-4">
                    <p className="text-gray-700 mb-4">{step.content}</p>
                    
                    {/* Interactive Component */}
                    {step.interactive && (
                      <div className="mb-4">
                        {step.interactive}
                      </div>
                    )}
                    
                    {/* Reveal Section */}
                    {step.reveal && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h5 className="font-medium text-blue-900 mb-2">💡 Khám phá thêm:</h5>
                        <p className="text-blue-800 text-sm">{step.reveal}</p>
                      </div>
                    )}
                    
                    {/* Complete Button */}
                    {!isCompleted && (
                      <button
                        onClick={() => handleStepComplete(index)}
                        className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <span>Hoàn thành bước này</span>
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    )}
                    
                    {isCompleted && (
                      <div className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg shadow-md">
                        <div className="w-6 h-6 bg-white bg-opacity-30 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">✓</span>
                        </div>
                        <span className="font-bold">Đã hoàn thành!</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Completion Message */}
      {completedSteps.size === steps.length && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
          <div className="text-4xl mb-2">🎉</div>
          <h4 className="font-bold text-green-900 mb-2">Chúc mừng!</h4>
          <p className="text-green-800">Bạn đã hoàn thành tất cả các bước khám phá!</p>
        </div>
      )}
    </div>
  )
}

export default InteractiveExploration